import Component from '@glimmer/component';
import { service } from '@ember/service';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { or, eq } from 'ember-truth-helpers';
import dayjs from 'dayjs';

import type { SearchResult } from '../../types/search';
import type RouterService from '@ember/routing/router-service';

interface SearchResultSignature {
  Args: {
    result: SearchResult;
    position: number;
    query?: string;
    onResultClick?: (result: SearchResult, position: number) => void;
  };
}

export default class SearchResultComponent extends Component<SearchResultSignature> {
  @service declare router: RouterService;

  get contentTypeClass() {
    const typeMap = {
      article: this.args.result.category || 'gaming-news',
      team: 'e-sport',
      player: 'e-sport',
      tournament: 'e-sport',
      match: 'e-sport',
    };
    return typeMap[this.args.result.type] || 'gaming-news';
  }

  get tagLabel() {
    const labelMap = {
      article: this.getCategoryLabel(this.args.result.category),
      team: 'Team',
      player: 'Player',
      tournament: 'Tournament',
      match: 'Match',
    };
    return labelMap[this.args.result.type] || 'Article';
  }

  get displayTitle() {
    const highlighted = this.args.result.highlightedFields?.title;
    if (highlighted && highlighted.trim()) {
      return highlighted;
    }
    return this.args.result.title || '';
  }

  get displayExcerpt() {
    const highlighted = this.args.result.highlightedFields?.excerpt;
    if (highlighted && highlighted.trim()) {
      return highlighted;
    }
    return this.args.result.excerpt || '';
  }

  get formattedDate() {
    if (!this.args.result.publishedDate) return null;
    return dayjs(this.args.result.publishedDate).format('MMMM D, YYYY');
  }

  get displayImage() {
    const result = this.args.result;
    
    // For tournaments, check tournament image first, then game image, then placeholder
    if (result.type === 'tournament') {
      if (result.image?.url) {
        return result.image.url;
      }
      if (result.game?.image?.url) {
        return result.game.image.url;
      }
      return '/img/posts/53.jpg';
    }
    
    // For matches, check game image first, then placeholder
    if (result.type === 'match') {
      if (result.game?.image?.url) {
        return result.game.image.url;
      }
      return '/img/posts/38.jpg'; // Match-specific placeholder
    }
    
    // For teams, check team logo first, then placeholder
    if (result.type === 'team') {
      if (result.logo?.url) {
        return result.logo.url;
      }
      return '/img/posts/37.jpg'; // Team placeholder
    }
    
    // For players, check player photo first, then placeholder
    if (result.type === 'player') {
      if (result.photo?.url) {
        return result.photo.url;
      }
      return '/img/posts/36.jpg'; // Player placeholder
    }
    
    // For articles, check cover image first, then placeholder
    if (result.type === 'article') {
      if (result.coverImage?.url) {
        return result.coverImage.url;
      }
      return '/img/posts/13.webp'; // Article placeholder
    }
    
    // For other types, use existing placeholder logic
    const imageMap = {
      article: '/img/posts/13.webp',
    };
    return imageMap[result.type] || '/img/posts/13.webp';
  }

  get showAuthor() {
    return this.args.result.type === 'article' && this.args.result.author;
  }

  get showDate() {
    return this.args.result.type === 'article' && this.formattedDate;
  }

  get typeSpecificInfo() {
    switch (this.args.result.type) {
      case 'team':
        return this.getTeamInfo();
      case 'player':
        return this.getPlayerInfo();
      case 'tournament':
        return this.getTournamentInfo();
      case 'match':
        return this.getMatchInfo();
      default:
        return null;
    }
  }

  private getCategoryLabel(category?: string) {
    const labels = {
      news: 'Gaming News',
      review: 'Game Reviews',
      esports: 'Esports',
    };
    return labels[category as keyof typeof labels] || 'Gaming News';
  }

  private getTeamInfo() {
    const result = this.args.result;
    let info = '';
    if (result.country) info += result.country;
    if (result.game?.name) info += info ? ` • ${result.game.name}` : result.game.name;
    return info;
  }

  private getPlayerInfo() {
    const result = this.args.result;
    let info = '';
    if (result.currentTeam?.name) info += result.currentTeam.name;
    if (result.role) info += info ? ` • ${result.role}` : result.role;
    return info;
  }

  private getTournamentInfo() {
    const result = this.args.result;
    let info = '';
    if (result.game?.name) info += result.game.name;
    if (result.prizePool) {
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0,
      }).format(result.prizePool);
      info += info ? ` • ${formatted}` : formatted;
    }
    return info;
  }

  private getMatchInfo() {
    const result = this.args.result;
    let info = '';
    if (result.game?.name) info += result.game.name;
    if (result.teams && result.teams.length > 0) {
      const teamNames = result.teams.map(team => team.name || team).join(' vs ');
      info += info ? ` • ${teamNames}` : teamNames;
    }
    return info;
  }

  handleClick = (event: Event) => {
    // Track the click
    this.args.onResultClick?.(this.args.result, this.args.position);

    // Don't prevent default navigation
    return true;
  };

  <template>
    <div class='post-preview landscape {{this.contentTypeClass}}' data-result-type='{{@result.type}}'>
      <a href='{{@result.url}}' {{on "click" this.handleClick}}>
        <div class='post-preview-img-wrap'>
          <figure class='post-preview-img liquid'>
            <img src='{{this.displayImage}}' alt='{{@result.title}}' />
          </figure>
        </div>
      </a>
      
      <a href='{{@result.url}}' class='tag-ornament' {{on "click" this.handleClick}}>{{this.tagLabel}}</a>
      
      <a href='{{@result.url}}' class='post-preview-title' {{on "click" this.handleClick}}>
        {{{this.displayTitle}}}
      </a>
      
      {{#if (or this.showAuthor this.showDate this.typeSpecificInfo)}}
        <div class='post-author-info-wrap'>
          <p class='post-author-info small light'>
            {{#if this.showAuthor}}
              By <span class='post-author'>{{@result.author}}</span>
              {{#if this.showDate}}<span class='separator'>|</span>{{/if}}
            {{/if}}
            {{#if this.showDate}}
              {{this.formattedDate}}
            {{/if}}
            {{#if this.typeSpecificInfo}}
              {{#if (or this.showAuthor this.showDate)}}<span class='separator'>|</span>{{/if}}
              {{this.typeSpecificInfo}}
            {{/if}}
          </p>
        </div>
      {{/if}}
      
      {{#if @result.excerpt}}
        <p class='post-preview-text'>
          {{{this.displayExcerpt}}}
        </p>
      {{/if}}
      
      {{#if @result.tags.length}}
        <div class='search-result-tags'>
          {{#each @result.tags as |tag|}}
            <span class='search-tag'>{{tag}}</span>
          {{/each}}
        </div>
      {{/if}}
    </div>
  </template>
}
